import {
  autoUpdate,
  flip,
  FloatingPortal,
  shift,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  useInteractions
} from "@floating-ui/react";
import classNames from "classnames";
import isArray from "lodash/isArray";
import noop from "lodash/noop";
import { Button, ButtonProps } from "PFComponents/button";
import DropDown, { DropdownOptions, DropDownProps } from "PFComponents/dropdown/dropdown";
import { IconProps } from "PFCore/components/icon";
import { getAriaProps } from "PFCore/helpers/get_aria_props";
import { IconName } from "PFTheme/graphics/icons";
import {
  AriaAttributes,
  CSSProperties,
  forwardRef,
  ReactNode,
  RefObject,
  useCallback,
  useRef,
  useState
} from "react";

import { ActionIcon } from "../action_icon";
import css from "./dropdown_button.module.scss";

type DropdownButtonProps = AriaAttributes & {
  children?: ReactNode;
  actionIcon?: IconName;
  actionIconColor?: IconProps["color"];
  icon?: IconName;
  iconPlacement?: ButtonProps["iconPlacement"];
  text?: string;
  small?: boolean;
  style?: CSSProperties;
  className?: string;
  disabled?: boolean;
  qaId?: string;
  options: DropdownOptions;
  buttonKind?: ButtonProps["kind"];
  /**
   * @deprecated Use one of the Button variants
   */
  buttonStyle?: CSSProperties;
  buttonClassName?: string;
  dropDownStyle?: CSSProperties;
  dropDownClassName?: string;
  dropdownProps?: Partial<DropDownProps>;
  handleChange: (item: any) => void;
  handleClose?: () => void;
  handleClick?: (event: any, menuShown: boolean) => void;
  floatingOptions?: Partial<UseFloatingOptions>;
  openByDefault?: boolean;
};

export const DropdownButton = forwardRef<HTMLElement, DropdownButtonProps>(
  (
    {
      actionIcon,
      actionIconColor,
      icon,
      iconPlacement,
      text,
      small,
      style,
      className,
      disabled = false,
      qaId = "Button",
      options,
      buttonKind,
      buttonStyle,
      buttonClassName,
      dropDownStyle,
      dropDownClassName,
      dropdownProps = {},
      children,
      handleChange,
      handleClose,
      handleClick = noop,
      floatingOptions,
      openByDefault = false,
      ...props
    }: DropdownButtonProps,
    ref
  ) => {
    const [menuShown, setMenuShown] = useState(openByDefault);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        setMenuShown(open);
        if (!open) {
          handleClose?.();
        }
      },
      [handleClose]
    );

    const { refs, floatingStyles, context } = useFloating({
      open: menuShown,
      onOpenChange: handleOpenChange,
      placement: "bottom-end",
      middleware: [flip(), shift()],
      whileElementsMounted: autoUpdate,
      ...floatingOptions
    });

    const dismiss = useDismiss(context);
    const { getFloatingProps } = useInteractions([dismiss]);

    const { ariaProps } = getAriaProps(props);
    const dropDownRef = useRef(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const defaultStyle: React.CSSProperties = {
      minWidth: 100,
      maxWidth: 300,
      textAlign: "left",
      position: "relative",
      display: "block",
      marginBottom: 0,
      ...dropDownStyle
    };

    // TODO: [PROF-7192] It should be refactored as a part of Dropdown refactor ticket
    // This is a hack to make the dropdown work with keyboard navigation
    // The same implementation is done in select.tsx
    // @ts-ignore
    const handleKeyDown = (event) => dropDownRef.current && dropDownRef.current.handleKeyDown(event);

    // Ref callback that combines refs.setReference and the forwarded ref
    const combinedRef = useCallback(
      (node: HTMLElement | null) => {
        refs.setReference(node);
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as RefObject<HTMLElement | null>).current = node;
        }
      },
      [ref, refs]
    );

    const commonProps = {
      ...ariaProps,
      disabled,
      "ref": combinedRef,
      qaId,
      "onClick": (event) => {
        event.stopPropagation();
        setMenuShown(!menuShown);
        handleClick && handleClick(event, !menuShown);
      },
      "onKeyDown": (event) => event.stopPropagation(),
      "aria-expanded": menuShown,
      "aria-haspopup": "true" as const
    };

    const button = actionIcon ? (
      <ActionIcon
        title={ariaProps["aria-label"]}
        name={actionIcon}
        size={small ? "sm" : "md"}
        color={actionIconColor}
        {...commonProps}
      />
    ) : (
      <Button
        {...(children ? { children } : { icon, text, iconPlacement })}
        small={small}
        kind={buttonKind}
        style={buttonStyle}
        className={buttonClassName}
        {...commonProps}
      />
    );

    return (
      <div
        tabIndex={-1}
        role="none"
        ref={menuRef}
        className={classNames(css.dropdownButton, className)}
        style={style}
        onKeyDown={handleKeyDown}
        data-dropdown-button-menu="true"
      >
        {button}
        {menuShown && (
          <FloatingPortal>
            <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 9999 }} {...getFloatingProps()}>
              <DropDown
                ref={dropDownRef}
                style={defaultStyle}
                rootClassName={dropDownClassName}
                options={options}
                handleChange={(item) => {
                  if (!isArray(item)) {
                    !item.keepOpen && setMenuShown(false);
                  }

                  handleChange && handleChange(item);
                }}
                handleClose={() => {
                  setMenuShown(false);
                  handleClose && handleClose();
                }}
                {...dropdownProps}
              />
            </div>
          </FloatingPortal>
        )}
      </div>
    );
  }
);

DropdownButton.displayName = "DropdownButton";
